/**
 * ตัวสั่ง sync engine จากหน้าจอหน้างาน (D-078)
 *
 * สอง trigger เท่านั้นตามที่ D-078 กำหนดสำหรับ Phase 1:
 *   - ผู้ปฏิบัติงานกด "ซิงค์ตอนนี้"
 *   - เครื่องกลับมาออนไลน์ (`false → true` จาก `useConnectivity`)
 *
 * **ไม่มี interval และไม่มี background sync ของ Service Worker** โดยเจตนา: ตัว
 * ระบายที่วิ่งเองเป็นระยะจะทำให้ปัญหาการซิงค์เกิดตอนไม่มีใครดูอยู่ ซึ่งเป็นสิ่งที่
 * เครื่องหน้างานรับไม่ได้ก่อนที่จะมีหน้าจอ conflict ให้คนตามงานได้จริง
 *
 * hook นี้ไม่รู้กติกาการส่งเลยแม้แต่น้อย — มันแค่เรียก `drainOfflineCommands()`
 * แล้วแปลผลเป็นข้อความ จำนวนคำสั่งบนหน้าจอยังมาจากคิวจริงผ่าน `usePendingSyncStatus`
 * เหมือนเดิม (`pendingSync.ts`) ไม่ใช่จากผลลัพธ์รอบนี้
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { drainOfflineCommands, type DrainResult } from '../lib/offline/syncEngine';

export type OfflineSyncState =
  | { readonly status: 'idle' }
  | { readonly status: 'syncing' }
  | { readonly status: 'done'; readonly result: DrainResult };

export interface OfflineSyncController {
  readonly state: OfflineSyncState;
  readonly syncNow: () => Promise<void>;
}

/**
 * ข้อความที่ผู้ปฏิบัติงานอ่านแล้วรู้ว่าต้องทำอะไรต่อ
 *
 * ห้ามพูดว่า "บันทึกสำเร็จ" กับอะไรที่ยังไม่ได้คำตอบจากเซิร์ฟเวอร์ และห้ามกลบ
 * `CONFLICT`/`REJECTED` ไว้ในคำว่า "ซิงค์แล้ว" — คำสั่งที่ต้องมีคนมาดูต้องถูกพูดถึง
 * ทุกครั้งที่มันเกิดขึ้น (`docs/SECURITY.md` Offline/Online Policy)
 */
export function describeOfflineSyncState(state: OfflineSyncState): string {
  if (state.status === 'idle') {
    return 'ยังไม่ได้ซิงค์ในรอบนี้';
  }
  if (state.status === 'syncing') {
    return 'กำลังส่งคำสั่งขึ้นเซิร์ฟเวอร์';
  }

  const { result } = state;
  if (result.status === 'skipped') {
    return result.reason === 'NOTHING_TO_SYNC'
      ? 'ไม่มีคำสั่งค้างในเครื่อง'
      : 'กำลังซิงค์อยู่แล้ว รอรอบนี้ให้จบก่อน';
  }

  if (result.status === 'synced') {
    const parts = [`เซิร์ฟเวอร์ตอบแล้ว ${result.sent} รายการ`, `รับ ${result.accepted}`];
    if (result.conflicted > 0) {
      parts.push(`ชนกฎ ${result.conflicted}`);
    }
    if (result.rejected > 0) {
      parts.push(`ถูกปฏิเสธ ${result.rejected}`);
    }
    if (result.unanswered > 0) {
      parts.push(`ยังไม่ได้คำตอบ ${result.unanswered} (รอรอบหน้า)`);
    }
    return parts.join(' · ');
  }

  switch (result.reason) {
    case 'UNAUTHENTICATED':
      return 'เซสชันหมดอายุ — เข้าสู่ระบบใหม่แล้วค่อยซิงค์ คำสั่งยังอยู่ในเครื่องครบ';
    case 'BATCH_REJECTED':
      return `เซิร์ฟเวอร์ไม่รับชุดคำสั่งนี้${result.rule === null ? '' : ` (${result.rule})`} — คำสั่งยังอยู่ในเครื่องครบ`;
    case 'SERVER_ERROR':
      return 'เซิร์ฟเวอร์ขัดข้อง — คำสั่งยังอยู่ในเครื่องครบ ลองใหม่ได้';
    case 'QUEUE_UNAVAILABLE':
      return 'คิวในเครื่องใช้งานไม่ได้ตอนนี้ — ยังไม่มีคำสั่งใบไหนถูกส่ง';
    case 'INVALID_RESPONSE':
      return 'คำตอบจากเซิร์ฟเวอร์อ่านไม่ออก — ถือว่ายังไม่รู้ผล คำสั่งยังอยู่ในเครื่องครบ';
    default:
      return 'ส่งไม่ถึงเซิร์ฟเวอร์ — คำสั่งยังอยู่ในเครื่องครบ ลองใหม่เมื่อสัญญาณกลับมา';
  }
}

export function useOfflineSync(online: boolean): OfflineSyncController {
  const [state, setState] = useState<OfflineSyncState>({ status: 'idle' });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const syncNow = useCallback(async () => {
    if (mounted.current) {
      setState({ status: 'syncing' });
    }
    const result = await drainOfflineCommands();
    if (mounted.current) {
      setState({ status: 'done', result });
    }
  }, []);

  /*
   * วิ่งเฉพาะตอน "เปลี่ยนจากออฟไลน์เป็นออนไลน์" ไม่ใช่ทุกครั้งที่ค่าเป็น true —
   * หน้าจอที่เปิดมาพร้อมสัญญาณอยู่แล้วไม่ใช่การ reconnect และการยิงตอน mount
   * จะทำให้ทุกครั้งที่สลับโหมดงานกลายเป็นการซิงค์รอบใหม่
   */
  const previousOnline = useRef(online);
  useEffect(() => {
    const reconnected = !previousOnline.current && online;
    previousOnline.current = online;
    if (reconnected) {
      void syncNow();
    }
  }, [online, syncNow]);

  return { state, syncNow };
}
